/**
 * Shared contract for the Nexudus "bootstrap" cache — the two public-site-host
 * routes that every portal server render depends on:
 *
 *   GET  /en/business                 (the location tree)
 *   POST /en/mobileapp/Configuration  (Name/Value config pairs)
 *
 * Measured against production on 10 Aug 2026: business serialises to 159,076
 * characters and configuration to 28,084 (418 entries, 171 with a null Value).
 * Both are stored in a single Azure Table property, which holds at most 32,768
 * characters — business only fits because `BruntwoodCacheTable` gzips values
 * that exceed that on their own, and configuration is within 5,000 characters
 * of needing the same treatment. An earlier estimate of "~22 KB" for business
 * is what let the 103.36.0 release ship a cache that could never write.
 * Re-measure before assuming either still fits.
 *
 * Entries are written into the shared Azure `BruntwoodCacheTable` by
 * companion-auth's CacheCop `BootstrapPrimer` and read by the portal, so the
 * key format and expiry must agree across projects — they live here.
 *
 * Design: the primer refreshes every 10 minutes but entries stay servable for 24 hours,
 * so a rate-limited or slow Nexudus degrades staleness rather than
 * availability. These two routes were behind 99.5% of the 21 Jul 2026
 * rate-limit storm — see `docs/topics/nexudus-incident-report-2026-07-21.md`.
 */

export type NexudusBootstrapEndpoint = 'business' | 'configuration';

/**
 * How long a cached entry may be served. Deliberately much longer than the
 * primer's refresh cadence: a failed refresh leaves the previous copy
 * standing instead of taking the portal down with it.
 */
export const NEXUDUS_BOOTSTRAP_CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;

/**
 * `host` may be a bare hostname, a host:port, or a full URL — the primer is
 * configured with `NEXUDUS_ENV_BASE_URL` (a URL) while the portal passes its
 * per-request host — so normalise to a lowercase hostname before keying.
 */
const normaliseHost = (host: string): string =>
  host
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .toLowerCase();

/**
 * Cache key for a bootstrap entry. Both responses are space-scoped, never
 * user-scoped (verified against the sandbox), so the key deliberately carries
 * no user identity — and writers must only ever store anonymously-fetched
 * responses to keep that true.
 */
export const nexudusBootstrapCacheKey = (endpoint: NexudusBootstrapEndpoint, host: string): string =>
  `nexudusBootstrap:${endpoint}:${normaliseHost(host)}`;
